'use client';

import { useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { FieldLabel } from '@/shared/components/ui/Field';
import { cn } from '@/shared/utils/cn';
import { caretAfterFormat, significantBefore } from '@/shared/utils/fieldFormats';
import {
  DEFAULT_DIAL,
  DIAL_OPTIONS,
  formatNational,
  joinPhone,
  splitPhone,
} from '@/shared/utils/phone';

/**
 * Champ de saisie d'un numéro de téléphone (#1344).
 *
 * Il remplace les champs libres du produit, qui ne disaient jamais de quel pays venait le numéro et
 * laissaient chaque écran inventer sa mise en forme.
 *
 * **La valeur reste une seule chaîne** (`+33 6 55 32 78 90`) : c'est ce que l'API attend, et
 * scinder le champ côté web obligerait chaque appelant à recomposer — donc à le faire différemment
 * quelque part.
 *
 * Deux comportements à connaître :
 *
 * - un numéro **déjà enregistré sans indicatif** s'affiche tel quel, sous l'indicatif par défaut,
 *   et n'est réécrit que si on le modifie. Le convertir à l'ouverture changerait la base à l'insu
 *   de qui ne fait que consulter une fiche ;
 * - **un champ vide reste vide** : l'indicatif seul ne s'enregistre pas. « +33 » stocké ressemble
 *   à un numéro et n'en est pas un.
 */
export function PhoneInput({
  id,
  label,
  value,
  onChange,
  required,
  error,
  hint,
  disabled,
  placeholder = '6 12 34 56 78',
  className,
}: {
  id?: string;
  label?: string;
  /** Numéro complet, tel qu'il est enregistré. */
  value: string | null | undefined;
  /** Reçoit le numéro complet, ou une chaîne vide. */
  onChange: (value: string) => void;
  required?: boolean;
  error?: boolean;
  hint?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const parsed = useMemo(() => splitPhone(value), [value]);
  /*
    L'indicatif choisi vit ici tant qu'aucun numéro n'est saisi : sans cela, choisir « Belgique »
    sur un champ vide n'aurait aucun effet visible — la valeur restant vide, la lecture rendrait
    « +33 » au rendu suivant, et le choix semblerait ignoré.
  */
  const [dial, setDial] = useState(parsed.dial || DEFAULT_DIAL);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeDial = parsed.national ? parsed.dial : dial;
  const option = DIAL_OPTIONS.find(o => o.dial === activeDial);

  /*
    Le champ **affiche** la forme lisible et **enregistre** la même valeur qu'avant (#1495).

    `splitPhone` compacte la partie nationale pour retrouver l'indicatif — c'est nécessaire, un
    `+33 6 52` ne se reconnaît pas autrement. Mais le champ affichait cette forme compactée : le
    numéro n'était donc jamais lisible, ni à la réouverture d'une fiche, ni juste après l'avoir tapé.
  */
  const displayed = formatNational(activeDial, parsed.national);

  const setNational = (input: string, caretAt?: number | null) => {
    const formatted = formatNational(activeDial, input);
    onChange(joinPhone(activeDial, formatted));
    if (caretAt == null) return;
    // Le curseur suit le chiffre saisi : sans report, React le repose en fin de champ à chaque frappe.
    const before = significantBefore(input, caretAt);
    const caret = caretAfterFormat(formatted, before);
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(caret, caret));
  };

  const setDialCode = (next: string) => {
    setDial(next);
    // Le numéro déjà saisi est reformaté selon le nouvel indicatif : les paires françaises ne sont
    // pas la forme belge, et laisser l'ancienne mise en forme donnerait un numéro qui n'existe
    // nulle part.
    onChange(parsed.national ? joinPhone(next, formatNational(next, parsed.national)) : '');
  };

  return (
    <div className={cn('relative', className)}>
      {label && (
        <FieldLabel htmlFor={id} className="mb-2 text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </FieldLabel>
      )}

      {/*
        La **même boîte** que `CustomInput` (#1371) : hauteur, rayon, bordure, ombre et anneau de
        focus. Ce champ composait la sienne — plus basse d'un cheveu, sans ombre, avec un autre
        anneau — et il se voyait aussitôt qu'un autre champ était posé à côté, ce que fait chaque
        formulaire du produit.
      */}
      <div
        className={cn(
          'border-input shadow-custom-sm flex h-[42px] items-center rounded-xl border bg-transparent transition-[color,box-shadow]',
          'focus-within:ring-primary-500 focus-within:ring-2 focus-within:ring-offset-1',
          error && 'border-destructive ring-destructive/20',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {/*
          Un `<select>` natif, et non une liste sur mesure : il s'ouvre au clavier, se cherche en
          tapant les premières lettres, et se rend correctement sur mobile — trois choses qu'une
          liste maison redemande à chaque fois.
        */}
        <div className="relative shrink-0">
          <select
            aria-label="Indicatif du pays"
            value={activeDial}
            disabled={disabled}
            onChange={e => setDialCode(e.target.value)}
            className="text-text-strong-950 h-[40px] cursor-pointer appearance-none rounded-l-xl bg-transparent py-0 pr-7 pl-3 text-base font-medium focus:outline-none md:text-sm"
          >
            {DIAL_OPTIONS.map(o => (
              <option key={o.code} value={o.dial}>
                {o.flag} {o.dial}
              </option>
            ))}
          </select>
          <ChevronDown className="text-text-soft-400 pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2" />
        </div>

        <span className="bg-input h-5 w-px shrink-0" aria-hidden />

        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
          ref={inputRef}
          value={displayed}
          onChange={e => setNational(e.target.value, e.target.selectionStart)}
          placeholder={placeholder}
          className="placeholder:text-muted-foreground h-[40px] min-w-0 flex-1 rounded-r-xl bg-transparent px-3 text-base outline-none md:text-sm"
        />
      </div>

      {hint && (
        <p className={cn('mt-1.5 text-[12px]', error ? 'text-red-600' : 'text-text-soft-400')}>
          {hint}
        </p>
      )}
      {option && <span className="sr-only">{option.label}</span>}
    </div>
  );
}

export default PhoneInput;
